import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import { useLocalization } from '../../common/components/LocalizationProvider';
import dayjs from 'dayjs';

// Lightweight DateTime field: prefer MUI DateTimePicker when available,
// fallback to native input[type=datetime-local]. Emits ISO strings.
const LocalizedDateTimeField = ({
  value, // ISO string
  onChange, // receives ISO string or null
  label,
  disabled,
  inputRef,
  className,
  endAdornment,
  useNative = false,
}) => {
  const { htmlLang, language } = useLocalization();
  const [PickerComponents, setPickerComponents] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (useNative) return undefined;
    (async () => {
      try {
        const [DateTimePickerMod, LocalizationProviderMod, AdapterDayjsMod] = await Promise.all([
          import('@mui/x-date-pickers/DateTimePicker'),
          import('@mui/x-date-pickers/LocalizationProvider'),
          import('@mui/x-date-pickers/AdapterDayjs'),
        ]);
        if (!mounted) return;

        // Normalize exports: some bundlers provide default, some named exports
        const DateTimePicker = DateTimePickerMod?.default || DateTimePickerMod?.DateTimePicker || DateTimePickerMod;
        const LocalizationProvider = LocalizationProviderMod?.default || LocalizationProviderMod?.LocalizationProvider || LocalizationProviderMod;
        const AdapterDayjs = AdapterDayjsMod?.default || AdapterDayjsMod?.AdapterDayjs || AdapterDayjsMod;

        setPickerComponents({ DateTimePicker, LocalizationProvider, AdapterDayjs });
      } catch {
        // If dynamic import fails (pickers not installed), fall back to native input
        setPickerComponents(null);
      }
    })();
    return () => { mounted = false; };
  }, [useNative]);

  const renderNative = () => (
    <TextField
      fullWidth
      label={label}
      value={value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : ''}
      onChange={(e) => onChange && onChange(e.target.value ? dayjs(e.target.value).toISOString() : null)}
      disabled={disabled}
      inputRef={inputRef}
      InputProps={{
        inputProps: { type: 'datetime-local', lang: htmlLang },
        endAdornment,
      }}
      className={className}
      InputLabelProps={{ shrink: true }}
    />
  );

  if (!PickerComponents) return renderNative();

  const { DateTimePicker, LocalizationProvider, AdapterDayjs } = PickerComponents;
  const selected = value ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
      <DateTimePicker
        label={label}
        value={selected}
        onChange={(newVal) => {
          if (!newVal) return onChange && onChange(null);
          const iso = dayjs(newVal).toISOString();
          onChange && onChange(iso);
        }}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            inputRef={inputRef}
            className={className}
            InputProps={{ ...params.InputProps, endAdornment }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default LocalizedDateTimeField;
